import type { LanguagesEnum } from '@/api/enums';
import type { PhishingTranslationSSEEvent } from '@/client/campaigns';

// The job signals completion twice: a terminal progress event whose tallies
// already account for every language, and a separate completed event right
// after. The UI must not depend on that second event alone — it can be missed
// (a page refresh resubscribes after the job has finished, a burst or a
// reconnect drops it), leaving the spinner turning at 100% with every language
// done. Finalising off whichever of the two arrives first fixes that.
export const resolvePhishingTranslationCompletion = (
  event: PhishingTranslationSSEEvent,
): { failedLanguages: LanguagesEnum[] } | null => {
  if (event.event === 'phishing-translation.completed') {
    return { failedLanguages: event.failedLanguages ?? [] };
  }

  if (event.event === 'phishing-translation.progress') {
    const accounted =
      (event.completedLanguages?.length ?? 0) +
      (event.failedLanguages?.length ?? 0);

    if (event.totalLanguages > 0 && accounted >= event.totalLanguages) {
      return { failedLanguages: event.failedLanguages ?? [] };
    }
  }

  return null;
};

// Backstop for the case where the job finishes before the browser's SSE
// subscription connects, so no progress or completed event is ever received.
// While translating, the status endpoint is polled; once it reports the job is
// no longer running the spinner is finalised from that instead of waiting for
// an event that already came and went. The settle window guards the brief
// moment right after enqueue, before the job shows up in the queue, so a
// not-yet-started job is not mistaken for a finished one.
export const shouldFinalizeFromStatus = (
  status: { active?: boolean } | undefined,
  elapsedMs: number,
  settleMs: number,
): boolean => !!status && status.active === false && elapsedMs >= settleMs;
